/**
 * Auth state. Single source of truth for "is someone signed in" across the
 * SPA. Hits /api/me once per app session and caches; 401 → anonymous.
 *
 * This complements entitlements.ts (which answers "what is this user
 * entitled to"). Here we answer "is there a user at all", which drives the
 * anonymous-vs-signed-in gating: the picker is open to everyone, but save,
 * custom-name, download, describe, timeline, chain, account require a user.
 *
 * After a successful login/signup or logout, call setAuthUser()/clearAuth()
 * so dependent components re-read without a full reload.
 */

import { useEffect, useState } from 'react';
import type { User } from '@mydailyhue/shared';
import { api, ApiError } from './api';

interface MeResponse {
  user: User;
}

type AuthState = { user: User | null; loading: boolean };

let cached: User | null = null;
let resolved = false;
let pending: Promise<User | null> | null = null;
const listeners = new Set<(u: User | null) => void>();

function emit(u: User | null) {
  for (const fn of listeners) fn(u);
}

async function load(): Promise<User | null> {
  if (resolved) return cached;
  if (pending) return pending;
  pending = (async () => {
    try {
      const me = await api<MeResponse>('/api/me');
      cached = me.user ?? null;
    } catch (e) {
      // 401 = anonymous; any other error → treat as anonymous, don't block UI.
      void (e instanceof ApiError);
      cached = null;
    } finally {
      resolved = true;
      pending = null;
    }
    return cached;
  })();
  return pending;
}

/** Set the signed-in user after login/signup; notifies all hooks. */
export function setAuthUser(user: User) {
  cached = user;
  resolved = true;
  emit(user);
}

/** Clear auth after logout; notifies all hooks. */
export function clearAuth() {
  cached = null;
  resolved = true;
  emit(null);
}

export function useAuth(): { user: User | null; signedIn: boolean; loading: boolean } {
  const [state, setState] = useState<AuthState>({
    user: cached,
    loading: !resolved,
  });

  useEffect(() => {
    let alive = true;
    const onChange = (u: User | null) => {
      if (alive) setState({ user: u, loading: false });
    };
    listeners.add(onChange);
    void load().then((u) => {
      if (alive) setState({ user: u, loading: false });
    });
    return () => {
      alive = false;
      listeners.delete(onChange);
    };
  }, []);

  return { user: state.user, signedIn: state.user !== null, loading: state.loading };
}
